<template>
  <collapse
    :key="`listViewExpandGroup${itemId}`"
    :icon-active="collapseOptions.iconActive"
    :icon-inactive="collapseOptions.iconInactive"
    :icon-position="collapseOptions.iconPosition"
    align-icon="start"
    class="border-b border-gray-20 product-list-view-item"
    :default-active="false"
    qaid="plpListViewItemContainer"
  >
    <collapse-item
      :name="`listViewExpandGroup${itemId}`"
      :qaid="`listViewExpandGroup${itemId}`"
      :is-active="isActive"
      :disabled="product && product.isArchived"
      @state-changed="stateChanged"
    >
      <template slot="title">
        <div class="media-object mb-0">
          <div class="media-object-section product-image w-32" qaid="plpListViewItemImage">
            <a :href="productUrl">
              <img class="w-full h-auto lazyload" :src="resizedImageUrl" :alt="product.displayName" />
            </a>
            <product-compare-button
              v-if="isCompareButtonVisible"
              small
              :product="product"
              :base-properties="baseProperties"
              class="mt-1"
              qaid="plpListViewItemCompareButton"
            />
          </div>
          <div class="media-object-section" style="--color-selector-outer-dimensions: 1em;" qaid="plpListViewItemInfo">
            <product-badge-year
              :product="product"
              :base-properties="baseProperties"
              class="mb-1"
              :qaid="`listViewItem${itemId}`"
            />
            <p class="font-medium text-lg mb-0 mt-25 leading-tight" qaid="plpListViewItemName">
              <a :href="productUrl">{{ product.displayName }}</a>
            </p>
            <p class="product-card__price text-sm mt-0 mb-2">
              <a :href="productUrl" class="no-underline" qaid="plpListViewItemPrice">
                <span class="product-card__price--sale">{{ price.sale }}</span>
                <span v-if="price.adv" class="product-card__price--adv">{{ price.adv }}</span>
              </a>
            </p>
            <div v-if="swatches" class="product-card__swatches mb-0" qaid="plpListViewItemSwatches">
              <swatches
                :colors="swatches"
                :radio-name="`swatches-${product.code}`"
                class="mb-0"
                @color-changed="changeImage"
              />
            </div>
          </div>
        </div>
      </template>

      <trek-loading :is-loading="isLoading">
        <div v-if="product.productGroups && product.isReady" data-plp-grid>
          <div
            v-for="(productGroup, productGroupIndex) in product.productGroups"
            :key="productGroupIndex"
            class="b2b-grid-group__section pl-4"
            :product-group-index="productGroupIndex"
          >
            <collapse
              :icon-active="collapseOptions.iconActive"
              :icon-inactive="collapseOptions.iconInactive"
              :icon-position="collapseOptions.iconPosition"
              align-icon="center"
              :default-active="false"
              :value="productGroupIndex"
            >
              <bulk-product-grid
                :hide-unavailable="hideUnavailable"
                :base-properties="baseProperties"
                :image-size="{width: 192, height: 144}"
                image-format="pjpeg"
                image-quality="60,1"
                image-unsharp-mask
                :is-plp="true"
                :base-product="product.code"
                :product-group-index="productGroupIndex"
                :product-type="productGroup.productType"
                :product-group="productGroup"
                :apple-table-headers="appleTableHeaders"
                @update-stock-watch-state="updateStocks"
                @update-cart-or-list-column="updateColumns"
              />
            </collapse>
          </div>
        </div>
      </trek-loading>
    </collapse-item>
  </collapse>
</template>

<script>
import productCard from './mixins/product-card';
import viewSize from '@/mixins/view-size';
import BulkProductGrid from '@/components/containers/bulk-product/BulkProductGrid';
import TrekLoading from '@/components/TrekLoading';
import Collapse from '@/components/Collapse';
import CollapseItem from '@/components/CollapseItem';
import {mapMutations, mapActions, mapState} from 'vuex';
import get from 'lodash/get';
import {updateStockWatchStatus} from '@/components/containers/bulk-product/utils/warehouses';

export default {
  name: 'ProductListViewItem',
  components: {TrekLoading, BulkProductGrid, Collapse, CollapseItem},
  mixins: [productCard, viewSize],

  props: {
    appleTableHeaders: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      collapseOptions: {
        iconActive: 'indeterminate_check_box',
        iconInactive: 'add_box',
        iconPosition: 'left',
      },
      hideUnavailable: false,
    };
  },

  computed: {
    ...mapState('plp', ['collapseState']),

    isActive() {
      return get(this.collapseState, this.product.code, false);
    },

    isLoading() {
      return (
        get(this.collapseState, this.product.code) === true && (!this.product.productGroups || !this.product.isReady)
      );
    },

    resizedImageUrl() {
      return this.currentImageUrl.replace('1360', '192').replace('1020', '144');
    },
  },

  methods: {
    updateStocks(payload) {
      if (payload && payload.item) {
        updateStockWatchStatus(this.product.productGroups, payload.item);
      }
    },

    stateChanged(isActive) {
      this.$emit('state-changed', isActive);
    },

    updateColumns(resData) {
      const responseEntries = resData.response;
      const colName = resData.colName;
      this.product.productGroups.forEach((productGroup) => {
        const products = productGroup.products;
        this.$store.dispatch('skuGrid/updateCartOrListColumn', {products, responseEntries, colName}).then((res) => {
          productGroup.products = res;
        });
      });
    },
    ...mapActions('skuGrid', ['updateCartOrListColumn']),
    ...mapMutations('skuGrid', ['updateCollapseState', 'clearBaseProduct']),
  },
};
</script>

<style lang="scss">
.product-list-view-item {
  ul.swatches {
    justify-content: left;
    margin-left: 0;
  }
}

.b2b-grid-group__section:last-child {
  .el-collapse-item__header,
  .el-collapse-item__wrap {
    border-bottom-color: transparent;
  }
}
</style>
